import { useEffect, useState, useRef } from "react";
import CheckBox from "./CheckBox";
import { focusElement } from "../js/utils";
import "../css/InputData.css";
import "../css/InputIconSearch.css";
import "../css/InputIconSearchPanel.css";
import "../css/InputIconSearchPanelMultiSelection.css";

let options = ["OPTOMETRÍA", "VISTA", "SOL", "LENTE DE CONTACTO", "AUDÍFONOS"];

export default function InputIconSearchPanelCustomSelection(props) {
  const [togglePanel, setTogglePanel] = useState(false);
  const [optionSelected, setOptionSelected] = useState(props.selectedOptions);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [inputSearch, setInputSearch] = useState("");
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);
  const inputSearchRef = useRef("");

  const getInputSize = () => {
    const sizeClass = {
      0: "input-full",
      1: "input-small",
      2: "input-medium",
      3: "input-large",
      4: "input-super-small",
    };
    return sizeClass[props.size] || "";
  };

  const searchOptions = () => {
    if (inputSearch === optionSelected) {
      setTogglePanel(false);
    }
  };

  const getOption = (op, toggle) => {
    let option = "";
    switch (op) {
      case "OPTOMETRÍA":
        option = toggle ? "OptometriaSi" : "OptometriaNo";
        break;
      case "VISTA":
        option = toggle ? "VistaSi" : "VistaNo";
        break;
      case "SOL":
        option = toggle ? "SolSi" : "SolNo";
        break;
      case "LENTE DE CONTACTO":
        option = toggle ? "LenteContactoSi" : "LenteContactoNo";
        break;
      case "AUDÍFONOS":
        option = toggle ? "AudifonosSi" : "AudifonosNo";
        break;
      default:
        break;
    }
    return option;
  };

  useEffect(() => {
    if (props.inactive) {
      setTogglePanel(false);
    }
  }, [props.inactive]);

  useEffect(() => {
    searchOptions();
  }, [inputSearch]);

  useEffect(() => {
    setOptionSelected(props.selectedOptions);
  }, [props.selectedOptions]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!togglePanel) return;

      if (e.key === "Escape") {
        setTogglePanel(false);
      }
    };

    const handleClickOutside = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !inputRef.current.contains(e.target)
      ) {
        setTogglePanel(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [togglePanel, highlightedIndex, options]);

  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      const highlightedOption = optionRefs.current[highlightedIndex];
      const panel = panelRef.current;
      if (panel && highlightedOption) {
        const optionTop = highlightedOption.offsetTop;
        const optionBottom = optionTop + highlightedOption.clientHeight;
        const panelTop = panel.scrollTop;
        const panelBottom = panelTop + panel.clientHeight;

        if (optionTop < panelTop) {
          panel.scrollTop = optionTop;
        } else if (optionBottom > panelBottom) {
          panel.scrollTop = optionBottom - panel.clientHeight;
        }
      }
    }
  }, [highlightedIndex]);

  useEffect(() => {
    setInputSearch("");
    setOptionSelected(props.selectedOptions);
  }, [options]);

  const handleSelectOption = (i, option) => {
    let selectedOption = "";
    let oppositeOption = "";

    switch (i) {
      case 0:
        selectedOption = option
          ? { id: "OptometriaSi", text: "Optometría sí" }
          : { id: "OptometriaNo", text: "Optometría no" };
        oppositeOption = option ? "OptometriaNo" : "OptometriaSi";
        break;
      case 1:
        selectedOption = option
          ? { id: "VistaSi", text: "Vista sí" }
          : { id: "VistaNo", text: "Vista no" };
        oppositeOption = option ? "VistaNo" : "VistaSi";
        break;
      case 2:
        selectedOption = option
          ? { id: "SolSi", text: "Sol sí" }
          : { id: "SolNo", text: "Sol no" };
        oppositeOption = option ? "SolNo" : "SolSi";
        break;
      case 3:
        selectedOption = option
          ? { id: "LenteContactoSi", text: "Lente de contacto sí" }
          : { id: "LenteContactoNo", text: "Lente de contacto no" };
        oppositeOption = option ? "LenteContactoNo" : "LenteContactoSi";
        break;
      case 4:
        selectedOption = option
          ? { id: "AudifonosSi", text: "Audífonos sí" }
          : { id: "AudifonosNo", text: "Audífonos no" };
        oppositeOption = option ? "AudifonosNo" : "AudifonosSi";
        break;
      default:
        break;
    }

    setOptionSelected((prev) => {
      const isSelected = prev.some((op) => op.id === selectedOption.id);

      // Si ya está seleccionado, lo quitamos
      if (isSelected) {
        return prev.filter((op) => op.id !== selectedOption.id);
      }

      // Si no está seleccionado, lo agregamos Y eliminamos el opuesto
      return [...prev.filter((op) => op.id !== oppositeOption), selectedOption];
    });

    props.onChange((prev) => {
      const isSelected = prev.some((op) => op.id === selectedOption.id);

      if (isSelected) {
        return prev.filter((op) => op.id !== selectedOption.id);
      }

      return [...prev.filter((op) => op.id !== oppositeOption), selectedOption];
    });
  };

  return (
    <div className="input-search-panel-container">
      <div
        className={`input-data input-icon-search ${getInputSize()} ${
          props.inactive ? "input-search-inactive" : ""
        }`}
        ref={inputRef}
      >
        <input
          id={props.id}
          tabIndex={props.tabIndex}
          type={props.type ? props.type : "text"}
          disabled={props.inactive}
          placeholder={props.placeholder}
          onChange={(e) => {
            const value = e.target.value.toUpperCase();
            inputSearchRef.current = value;
            setInputSearch(value);

            const indexMatch = options.findIndex((opt) => opt.includes(value));
            setHighlightedIndex(indexMatch >= 0 ? indexMatch : -1);
          }}
          onClick={() => {
            setTogglePanel(true);
          }}
          value={inputSearch}
        ></input>
        {inputSearch !== "" && (
          <img
            src="/images/close-dark.svg"
            loading="lazy"
            width="14"
            height="14"
            alt="icon"
            className="input-icon-search-clear"
            onClick={() => {
              setInputSearch("");
              setHighlightedIndex(-1);
            }}
          />
        )}
        <img
          src="/images/list-blue.svg"
          loading="lazy"
          width="14"
          height="14"
          alt="icon"
          className="input-icon-search-clear"
          style={{ marginLeft: "10px" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!props.inactive) {
              setTogglePanel((prevToggle) => !prevToggle);
              setHighlightedIndex(-1);
              setInputSearch("");
              focusElement(props.tabIndex);
            }
          }}
        />
      </div>
      {togglePanel && (
        <div className="input-search-panel" ref={panelRef}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              paddingLeft: 175,
              gap: 8,
            }}
          >
            <span className="title-h2">Sí</span>
            <span className="title-h2">No</span>
          </div>
          {options.map((e, i) => {
            return (
              <div
                className={`search-result-info ${
                  highlightedIndex === i ||
                  (e.includes(inputSearch) && inputSearch !== "")
                    ? "search-result-info-selected"
                    : ""
                }`}
                style={{ justifyContent: "flex-start", gap: "8px" }}
                key={i}
                ref={(el) => (optionRefs.current[i] = el)}
              >
                <span style={{ width: "160px" }}>{e}</span>
                <CheckBox
                  active={optionSelected
                    .map((item) => item.id)
                    .includes(getOption(e, true))}
                  onClick={() => {
                    handleSelectOption(i, true);
                  }}
                ></CheckBox>
                <CheckBox
                  active={optionSelected
                    .map((item) => item.id)
                    .includes(getOption(e, false))}
                  onClick={() => {
                    handleSelectOption(i, false);
                  }}
                ></CheckBox>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
